<script>
export default {
	name: 'SubTabMadaniSubsidy',
};
</script>

<script setup>
import RadioButton from '~/components/form/RadioButton.vue';
import SubTabBookFlight from '~/components/BookingWidget/FlightSearch/SubTabBookFlight.vue';
import { i18nGlobal } from '~/logic/i18n.js';

import AppSpinner from '~/components/AppSpinner.vue';
import { useAuthStore } from '~/logic/auth/auth-store.js';
import { useBookingWidgetUrlQuery } from '~/logic/composables/booking-widget/useBookingWidgetUrlQuery.js';
import { isMobileViewport } from '~/logic/composables/breakpoints.js';

const props = defineProps({
	hideTodayDateIndicator: { type: Boolean, default: false },
	subSidyMadaniGeneralDetails: { type: Object, default: null },
	subSidyMadaniOndSelection: { type: Object, default: null },
	subSidyMadaniPassengerSelection: { type: Object, default: null },
	subSidyMadaniDatePicker: { type: Object, default: null },
	subSidyMadaniPromoCode: { type: Object, default: null },
	subSidyMadaniEnrichAlwaysOn: { type: Object, default: null },
	
	disableGeolocation: { type: Boolean, default: false },
	subHeaderTooltipCTATextMobileLabel: { type: String, default: '' },
	fromQuestionMobile: { type: String, default: '' },
	toQuestionMobile: { type: String, default: '' },
	datePickerQuestionMobile: { type: String, default: '' },
	disableCalendarPickerAnimation: { type: Boolean, default: false },
	passengerSelectionQuestion: { type: String, default: '' },
	flightSearchApiUrl: { type: String, default: '' },
	cabinClassCashList: { type: Array, default: () => [] },
	cabinClassMilesList: { type: Array, default: () => [] },
	componentIdAA: { type: String, default: '' },
	togglePointsApiURL: { type: String, default: '' },
	enableMattaToggleButton: { type: Boolean, default: false },
	mattaToggleButtonLabel: { type: String, default: '' },
	enableONDLocaleTranslation: { type: Boolean, default: false },
	countryVariants: { type: Array, default: () => [] },
	payByEnrichPoints: { type: Boolean, default: false },
	cabinClassMilesOWList: { type: Array, default: () => [] },
	enableGlassDesign: { type: Boolean, default: false },
});

const emit = defineEmits([
	'save-search-data',
	'show-bst-overlay',
	'form-submission',
	'update:active-sub-tab',
]);

const {
	isLoggedIn,
} = useAuthStore();

const activeSubOption = ref(null);
const activeSubTab = ref('redeem-flight');

const subTabMalaysiaRef = ref(null);
const subTabOneWorldRef = ref(null);

const restorePastSearch = async (searchData) => {
	activeSubOption.value = searchData.redeemFlightType;
	await currentSubTabRef.value?.restorePastSearch(searchData);
};
const updateLocationFromTo = async (payload) => {
	currentSubTabRef.value?.updateLocationFromTo(searchData);
};
const isExpanded = async (payload) => {
	currentSubTabRef.value?.isExpanded(searchData);
};

const handleQueryChange = (query) => {
	if (!query) return;

	const newSubTab = query.subtab ?? '';
	const newSubOption = query.suboption ?? '';
	if (newSubTab) {
		activeSubTab.value = newSubTab;
		emit('update:active-sub-tab', newSubTab);
	}
};

const setupTabWatcher = (mainTabName, handler) => {
	const { registerQueryChange } = useBookingWidgetUrlQuery({ mainTabName });
	registerQueryChange(handler);
};

setupTabWatcher('flight-search-tab', handleQueryChange);
setupTabWatcher('enrich-flight-search', handleQueryChange);
onMounted(() => {
	nextTick(() => {
		// only set default if URL/watchers didn’t already set it
		
	});
});


defineExpose({
	restorePastSearch,
	updateLocationFromTo,
	isExpanded,
});


</script>

<template>
<div
	ref="rootEl"
	v-focus-within
	class="SubTabRedeemFlight flight-search-subcomponent"
	tabindex="-1"
	:class="{
		'isEditorMode': isEditorMode,
	}"
	@focus-within="handleRootFocusWithin"
	@blur-within="handleRootBlurWithin"
>
	<div>
		<h5 v-if="isEditorMode" class="title-editor-mode">
			<icon-fas-plane class="mr-2 fill-current inline-block" />
			<span>Booking Widget: Flight Search</span>
		</h5>
		<div>
			<!-- Content Panels -->
			<div>
				<SubTabBookFlight
					:isMadaniSubsidy="true"
					:hideTodayDateIndicator="props.hideTodayDateIndicator"
					:subHeaderTabDescription="props.subSidyMadaniGeneralDetails?.subHeaderTabDescription"
					:subHeaderTabTooltipBody="props.subSidyMadaniGeneralDetails?.subHeaderTabTooltipBody"
					:subHeaderTabTooltipLabel="props.subSidyMadaniGeneralDetails?.subHeaderTabTooltipLabel"
					:note="props.subSidyMadaniGeneralDetails?.note"
					:searchFlightCTAText="props.subSidyMadaniGeneralDetails?.searchFlightCTAText"
					:ondSelection="props.subSidyMadaniOndSelection"
					:additionalONDFilter="props.subSidyMadaniGeneralDetails?.ondFilter"
					:datePicker="props.subSidyMadaniDatePicker"
					:promoCodeLabel="props.subSidyMadaniPromoCode?.promoCodeLabel"
					:promoCodeErrorMessage="props.subSidyMadaniPromoCode?.promoCodeErrorMessage"
					:supersedePromoCode="props.subSidyMadaniPromoCode?.supersedePromoCode"
					:passengerSelection="props.subSidyMadaniPassengerSelection"
					:subHeaderTooltipCTATextMobileLabel="props.subHeaderTooltipCTATextMobileLabel"
					:fromQuestionMobile="props.fromQuestionMobile"
					:madaniCustomOndUrl="props.subSidyMadaniGeneralDetails?.customONDURLPath"
					:madaniFormTargetUrl="props.subSidyMadaniGeneralDetails?.madaniFormTargetUrl"
					:toQuestionMobile="props.toQuestionMobile"
					:flightSearchApiUrl="props.flightSearchApiUrl"
					:cabinClassCashList="props.cabinClassCashList"
					:cabinClassMilesList="props.cabinClassMilesList"
					:togglePointsApiURL="props.togglePointsApiURL"
					:enableMattaToggleButton="props.enableMattaToggleButton"
					:mattaToggleButtonLabel="props.mattaToggleButtonLabel"
					:hidePromoCodeField="props.subSidyMadaniPromoCode?.hidePromoCodeField"
					:enrichAlwaysOn="props.subSidyMadaniEnrichAlwaysOn?.enableFunctionality"
					:enablePostMattaCampaign="props.subSidyMadaniEnrichAlwaysOn?.enablePostMattaCampaign"
					:autoApplyEnrichAlwaysOn="props.subSidyMadaniEnrichAlwaysOn?.autoApplyEnrichAlwaysOn"
					:nonLoggedInMessageNote="props.subSidyMadaniEnrichAlwaysOn?.nonLoggedInMessageNote"
					:loggedInMessageNote="props.subSidyMadaniEnrichAlwaysOn?.loggedInMessageNote"
					:enrichAlwaysOnCountriesData="props.enrichAlwaysOnCountriesData"
					:disableGeolocation="props.disableGeolocation"
					:enableONDLocaleTranslation="props.isEnableLocale"
					tabName="subsidy-madani"
					:countryVariants="props.countryVariants"
					:cabinClassMilesOWList="props.cabinClassMilesOWList"
					:isIgnoreScroll="true"
					:enableGlassDesign="props.enableGlassDesign"
					@save-search-data="$emit('save-search-data', { isBSTTab: false, ...$event })"
					@form-submission="$emit('form-submission')"
				></SubTabBookFlight>
			</div>
		</div>
		
	</div>

	<Teleport v-if="isLoading" to="body">
		<div class="generic-backdrop !bg-white/80">
			<div class="flex items-center justify-center h-full w-full">
				<AppSpinner />
			</div>
		</div>
	</Teleport>
</div>
</template>


<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.SubTabMadani {

}
</style>